import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Landmark, Scale, Building2, DollarSign, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { FactBadge, FactCard, PositionBox, QuestionBox, SourceCite, FaqList } from '../components/FactCheck';
import { MoreDetails } from '../components/MoreDetails';
import { NextStepBand } from '../components/NextStepBand';

const ACCESSED = 'August 25, 2026';

const QUESTIONS = [
  'Is the current structure efficient?',
  'Are management responsibilities duplicated?',
  'Could responsibilities be reorganized?',
  'Are taxpayers receiving value?',
  'Are there unnecessary management layers?',
  'Can technology reduce administrative costs?',
];

const FOCUS = [
  { icon: Building2, word: 'THE POSITION.' },
  { icon: Scale, word: 'THE STRUCTURE.' },
  { icon: DollarSign, word: 'THE COST.' },
  { icon: Target, word: 'THE RESULTS.' },
];

// Every figure verified 2026-08-25 against the Ontario Public Sector Salary
// Disclosure (2025 records), Statistics Canada 2021 Census, and official
// municipal budget pages. Budget cells link to the official source when a
// clean verified total was not available.
const CAO_COMPARATORS = [
  { id: 'ckl', name: 'City of Kawartha Lakes', tier: 'Single-tier city', population: '79,247', salary: '$248,840', benefits: '$1,416', budget: '$472M (2026 total)', budget_url: 'https://www.kawarthalakes.ca/news-and-notices/posts/kawartha-lakes-council-adopts-the-2026-budget-centered-on-strategic-priorities/', highlight: true },
  { id: 'peterborough', name: 'City of Peterborough', tier: 'Single-tier city', population: '83,651', salary: '$330,327', benefits: '$1,808', budget: '$453.9M operating + $139.2M capital (2026)', budget_url: 'https://www.peterborough.ca/news/posts/city-of-peterborough-2026-budget-adopted/' },
  { id: 'norfolk', name: 'Norfolk County', tier: 'Single-tier county', population: '67,490', salary: '$286,278', benefits: '$759', budget: 'See official budget', budget_url: 'https://www.norfolkcounty.ca/news-and-notices/posts/norfolk-county-councils-budget-committee-adopts-2026-levy-capital-budget/' },
  { id: 'durham', name: 'Regional Municipality of Durham', tier: 'Regional (upper-tier over 8 municipalities)', population: '696,992', salary: '$364,165', benefits: '$10,763', budget: 'See official budget', budget_url: 'https://www.durham.ca/en/news/durham-regional-council-approves-the-2026-business-plans-and-budget.aspx' },
];

const FAQS = [
  { q: 'Is Steve attacking the CAO personally?', a: 'No. This review is about the position, the structure, the cost, and the results. It is not about any individual, and it is not a personal attack on an employee doing a job council created.' },
  { q: 'Is Steve promising to eliminate the CAO position?', a: 'No. Steve\'s position is that the CAO model deserves a serious, evidence-based review. Whether the structure should change is a question the review should answer, not a conclusion decided in advance.' },
  { q: 'Can a municipality even operate without a CAO?', a: 'The Municipal Act says a municipality MAY appoint a CAO. It is a choice, not a requirement. How a municipality organizes its administration is up to its council, within the law.' },
  { q: 'What would the review look at?', a: 'The organizational structure, management layers, spans of responsibility, administrative costs, duplication, how comparable municipalities are organized, and what results residents receive for the money.' },
];

export default function CaoReview() {
  return (
    <div>
      <PageHeader
        pageKey="cao-review"
        kicker="YOUR MONEY / YOUR COUNCIL"
        title="ARE TAXPAYERS GETTING VALUE FOR THEIR MONEY?"
        subtitle="A serious look at the City's top administrative structure: the position, the structure, the cost, and the results."
      />
      <RichSection id="cao-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* Focus banner */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="cao-focus-section">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cao-focus-banner">
          {FOCUS.map(({ icon: Icon, word }) => (
            <div key={word} className="rounded-xl bg-[#1a2744] p-4 sm:p-5 flex flex-col items-center text-center gap-2">
              <Icon className="h-6 w-6 text-white" />
              <p className="font-serif font-black text-white text-sm sm:text-base">{word}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-600 mt-4 max-w-3xl leading-relaxed" data-testid="cao-not-personal-note">This review is about how City government is organized and what it costs, not about any individual. It is not a personal attack on an employee.</p>

        {/* Verified facts */}
        <div className="mt-8">
          <SectionHeader kicker="THE FACTS FIRST" title="What the official records say" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FactCard kind="verified_fact" title="A CAO is optional under Ontario law"
              cite={{ source: 'Municipal Act, 2001 (Ontario), s. 229', year: '2001, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/01m25' }}>
              Section 229 of the Municipal Act says a municipality MAY appoint a chief administrative officer responsible for exercising general control and management of the affairs of the municipality for efficient and effective operation, plus other duties council assigns. It is a choice councils make, not a provincial requirement.
            </FactCard>
            <FactCard kind="official_source" title="Kawartha Lakes CAO disclosed pay (2025): $248,840"
              cite={{ source: 'Ontario Public Sector Salary Disclosure, 2025 records', year: '2025 records, published 2026', accessed: ACCESSED, url: 'https://www.ontario.ca/page/public-sector-salary-disclosure' }}>
              The Chief Administrative Officer was the City's highest disclosed salary in the 2025 records, at $248,840 (taxable benefits disclosed separately). The CAO leads the City's administration and reports to Council.
            </FactCard>
          </div>
        </div>

        {/* Steve's position */}
        <div className="mt-6">
          <PositionBox title="The CAO structure should be reviewed." testId="cao-position-band">
            <p>Not eliminated by slogan, and not defended by habit: reviewed, seriously and independently, against results and against how comparable municipalities organize their administration. If the review shows the structure serves taxpayers well, say so. If it shows duplication or unnecessary layers, fix it.</p>
          </PositionBox>
        </div>

        {/* The questions */}
        <div className="mt-8">
          <SectionHeader kicker="THE REVIEW" title="Questions the review should answer" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cao-questions-grid">
            {QUESTIONS.map((q, i) => <QuestionBox key={i} testId={`cao-question-${i + 1}`}>{q}</QuestionBox>)}
          </div>
        </div>

        {/* Comparators: verified from official disclosure records */}
        <div className="mt-8" data-testid="cao-comparators-section">
          <SectionHeader kicker="COMPARATORS" title="How do other municipalities compare?" dek="Verified from Ontario's official salary disclosure (2025 records) and official municipal budgets. Open More for the full table, sources, and the fairness note." />
          {/* Simple first: the headline salaries. More... opens the full verified table. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="cao-comparator-keyline">
            {CAO_COMPARATORS.map((r) => (
              <div key={r.id} className={`rounded-xl border p-3.5 ${r.highlight ? 'bg-[#eaf2ff] border-[#24406f]/30' : 'bg-white border-slate-200'}`} data-testid={`cao-key-${r.id}`}>
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 leading-tight">{r.name.replace('City of ', '').replace('Regional Municipality of ', '')}</p>
                <p className="text-lg sm:text-xl font-black text-[#1a2744] mt-1">{r.salary}</p>
                <p className="text-[11px] text-slate-500">CAO pay, 2025 records</p>
              </div>
            ))}
          </div>
          <MoreDetails testId="cao-comparators-more">
          <Card data-testid="cao-comparator-table-card">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="cao-comparator-table">
                <thead>
                  <tr className="bg-[#1a2744] text-left">
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">Municipality</th>
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">Government type</th>
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">Population (2021 Census)</th>
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">CAO disclosed salary (2025)</th>
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">Taxable benefits</th>
                    <th className="px-4 py-3 text-[11px] font-black uppercase tracking-wider text-white">Budget</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {CAO_COMPARATORS.map((row) => (
                    <tr key={row.name} className={row.highlight ? 'bg-[#FBF7EC]' : 'bg-white'} data-testid={`cao-comparator-row-${row.id}`}>
                      <td className="px-4 py-3 font-bold text-[#1a2744]">{row.name}</td>
                      <td className="px-4 py-3 text-slate-700">{row.tier}</td>
                      <td className="px-4 py-3 text-slate-700">{row.population}</td>
                      <td className="px-4 py-3 font-black text-[#1a2744]">{row.salary}</td>
                      <td className="px-4 py-3 text-slate-700">{row.benefits}</td>
                      <td className="px-4 py-3 text-slate-700">{row.budget_url ? (
                        <a href={row.budget_url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#C9A84C] decoration-2 underline-offset-2 font-semibold text-[#1a2744]">{row.budget}</a>
                      ) : row.budget}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <div className="mt-3 space-y-3">
            <SourceCite source="Ontario Public Sector Salary Disclosure (2025 records, published March 2026); Statistics Canada 2021 Census population counts; official municipal budget pages" year="2025 disclosure / 2021 Census / 2026 budgets" accessed={ACCESSED} url="https://www.ontario.ca/page/public-sector-salary-disclosure"
              methodology="CAO salaries are the amounts disclosed under the Public Sector Salary Disclosure Act for the 2025 calendar year; taxable benefits are disclosed separately. Populations are 2021 Census counts. Budget figures are shown only where verified from official municipal sources; otherwise the official budget page is linked. Employee counts are not shown because comparable verified totals were not available for all municipalities." />
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cao-fairness-note">
              <p className="text-sm text-slate-700 leading-relaxed"><span className="font-black text-[#1a2744]">Fairness note:</span> these municipalities differ in population, geography, services, and structure. A regional government like Durham serves hundreds of thousands of residents across eight lower-tier municipalities and delivers different services than a single-tier city. Population alone does not determine what a CAO should earn, and this table does not claim it does. The comparison exists to inform one question: is the Kawartha Lakes structure delivering value, and is a review warranted?</p>
            </div>
            <QuestionBox testId="cao-comparator-question">Compared with how similar single-tier municipalities organize and pay their administration, is the Kawartha Lakes model delivering the best value for residents?</QuestionBox>
          </div>
          </MoreDetails>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="cao-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
          <FaqList items={FAQS} testId="cao-faq" />
        </div>
      </section>

      {/* One clear next step */}
      <div className="pt-10">
        <NextStepBand
          title="See the disclosure numbers for yourself."
          buttonLabel="Explore the Data"
          to="/sunshine-list"
          secondaryLabel="Or read City Finances, Explained"
          secondaryTo="/city-finances"
          testId="cao-next-step"
        />
      </div>
    </div>
  );
}
